import { Route } from '@angular/router';
import { PageSeoData } from '@shared/helpers/seo.service';
import { AuthCanMatchGuard } from 'app/guards/auth.guard';
import { CopilotCanMatchGuard } from 'app/guards/copilot.guard';
import { SubscriptionCanMatchGuard } from 'app/guards/subscription.guard';
import { COPILOT_ENTRY_AUTHORITIES } from './copilot.constants';

/**
 * The /copilot route, as the router uses it. It lives HERE (next to the feature,
 * not inline in app-routing.module.ts) for two reasons:
 *  1. its entry authorities are derived from COPILOT_TABS, so definition and
 *     derivation sit side by side;
 *  2. a spec can import THIS object — the real guards + the real data — without
 *     importing app-routing.module.ts, which statically references all ~26 lazy
 *     page modules and pushes the karma build past its heap budget (the E8 spec
 *     did exactly that and OOM'd `npm test` at its 6000 MB ceiling).
 * app-routing.module.ts spreads this into the layout route's children.
 *
 * Copilot is a SELLABLE feature (bySubscription:true) — gated with the
 * subscription pattern like screener/risks/marketplace, PLUS
 * CopilotCanMatchGuard: SubscriptionCanMatchGuard's fallback lets any
 * authenticated user match when the tenant's sellable-module catalog has no AI
 * module, exposing an inert Copilot shell by deep link. CopilotCanMatchGuard
 * closes that hole while still honoring the early-access and upsell paths — do
 * NOT replace it with the hard AuthoritiesCanActivateGuard (no early-access
 * allowance — the very bug this route has churned on: #305 added it, #307
 * removed it). RLN2-708: also restricts to Reluna/trial, Preprod/pp, Lwam
 * contours (SiteName pattern, same as API Tokens). The navbar item is gated on
 * the same conditions.
 */
export const COPILOT_ROUTE: Route = {
  path: 'copilot',
  canMatch: [AuthCanMatchGuard, SubscriptionCanMatchGuard, CopilotCanMatchGuard],
  data: {
    seo: {
      pageTitleI18nKey: 'pages.copilot.title',
    } as PageSeoData,
    // E8 (review #5): the one_of entry set is DERIVED from COPILOT_TABS — the
    // union of every tab's authority plus the write/manage/run/approve surfaces
    // inside it (COPILOT_ENTRY_AUTHORITIES, the single source of truth in
    // copilot.constants.ts, shared with the navbar item). The hand-maintained
    // list that used to live in app-routing.module.ts was NARROWER than the tab
    // set (RECON*/ADMIN + ALERT_VIEW), so a REPORT_VIEW-only / ASK-only /
    // CIO_VIEW-only user could not open the page whose tab was theirs.
    // Do NOT inline a literal list here again — it drifts.
    //
    // S3b (review #5.2): this is the array the guards read on EVERY navigation, so
    // it is the FROZEN union object itself — not a `[...spread]` copy. A copy would
    // have re-opened exactly the hole S3b closes: freezing the exported constant
    // while handing the live gate a mutable clone, whose `.length = 0` empties the
    // one_of list (hasAuthorities([]) === TRUE, SubscriptionCanMatchGuard only gates
    // `if (authorities?.length)`) and makes /copilot public. Sharing one frozen
    // object also makes "route ≡ union" an identity, not a copy that can drift.
    authorities: COPILOT_ENTRY_AUTHORITIES,
    bySubscription: true,
  },
  loadChildren: async () => (await import('./copilot.module')).CopilotModule,
};
